import { fluidResolvers, fluidVaults } from '@1delta/data-sdk'
import lodash from 'lodash'

const { chunk } = lodash

/** Safe upper bound for how many vaults fit in a single getVaultsEntireData() call
 *  before hitting typical eth_call gas limits. Adjust if needed. */
export const VAULT_CHUNK_SIZE = 25

/**
 * Builds the multicall descriptor for Fluid public data.
 *
 * Two calls:
 *   [0] VaultResolver.getVaultsEntireData()        — every vault in one shot
 *   [1] LiquidityResolver.getAllOverallTokensData() — per-token Liquidity-layer
 *       state. Vaults borrow from the shared Liquidity Layer, so the IRM input
 *       for every vault rate is the **Liquidity-layer token utilization**
 *       (`lastStoredUtilization`), not any vault-level ratio.
 *
 * fTokens are intentionally NOT fetched: they are standalone ERC-4626 vaults
 * on the shared Liquidity Layer (not a lending market), so they live outside
 * this module.
 *
 * Resolver addresses are constant across chains but sourced via the SDK so a
 * future chain with a divergent address can be added without code changes.
 */
export const buildFluidCall = (chainId: string, _lender?: string) => {
  const resolvers = fluidResolvers()?.[chainId]
  if (!resolvers) return []

  return [
    {
      address: resolvers.vaultResolver,
      name: 'getVaultsEntireData',
      params: [],
    },
    {
      address: resolvers.liquidityResolver,
      name: 'getAllOverallTokensData',
      params: [],
    },
  ]
}

/**
 * Chunked fallback for chains where the single `getVaultsEntireData()` call
 * exceeds eth_call gas limits. Uses the cached vault address list from
 * `fluidVaults()` to issue one `getVaultEntireData(vault)` call per address,
 * grouped into chunks of `VAULT_CHUNK_SIZE`.
 *
 * The parser must be the chunked variant that knows to reassemble the
 * per-vault results into a VaultEntireData[].
 */
export const buildFluidCallChunked = (chainId: string) => {
  const resolvers = fluidResolvers()?.[chainId]
  if (!resolvers) return []

  const vaults = fluidVaults()?.[chainId] ?? {}

  return Object.keys(vaults).map((vaultAddress) => ({
    address: resolvers.vaultResolver,
    name: 'getVaultEntireData',
    params: [vaultAddress],
  }))
}

// `chunk` is exported for callers that want to split the per-vault calls
// across multiple multicall batches (e.g. one batch of `VAULT_CHUNK_SIZE`).
export { chunk }
